import React, { useEffect, useRef, useState } from 'react';
import { Plus, Edit, UserPlus, UserMinus, Shield, Settings, Trash, Trash2, Users as UsersIcon, Send, Ban } from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import SearchableSelect from '../components/SearchableSelect';
import Dropdown from '../components/Dropdown';
import { useAuth } from '../contexts/AuthContext';
import { formatRoleName } from '../utils/roleUtils';
import PasswordInput from '../components/PasswordInput';
import CTable from '../components/shared/c-table';
import CModal from '../components/shared/c-modal';
import { validatePasswordComplexity } from '../utils/validation';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';

// The role ceiling: nobody may grant, revoke or define more access than they hold. The
// server marks what this caller may act on (`assignable` roles, `grantable` permissions,
// `manageable` users) and refuses anything else with 403 ROLE_CEILING. Only an explicit
// `true` counts, so a missing flag never offers an action the server may refuse.
const canAssignRole = (role) => role?.assignable === true;
const canManageUser = (user) => user?.manageable === true;
const canGrantPermission = (perm) => perm?.grantable === true;

const ASSIGN_REASON = "You can't assign a role with more access than your own.";
const REMOVE_REASON = "You can't remove a role with more access than your own.";
const USER_REASON = "You can't change a user who has more access than you.";
const PERMISSION_REASON = "You can't grant a permission you don't hold yourself.";
const ROLE_REASON = "This role has access you don't hold, so you can't change it.";
const OWN_LINK_REASON = "You can't change your own employee link or department.";
const LINK_GRANT_REASON = 'Only someone who can edit every employee record can change this.';
const NO_ROLE_ERROR = 'Choose at least one role for this user.';

// Card-header strips for the Users / Roles CTables.
const UsersTableHeader = ({ count, statusFilter, onStatusChange }) => (
  <div className="flex items-center justify-between gap-4">
    <h3 className="text-lg font-medium text-gray-900">
      {count} User{count === 1 ? '' : 's'}
    </h3>
    <div className="flex items-center gap-2">
      <label htmlFor="user-status-filter" className="text-sm text-gray-600">Status</label>
      <Dropdown
        id="user-status-filter"
        data-testid="users-status-filter"
        value={statusFilter}
        onChange={onStatusChange}
        className="min-w-[160px]"
      >
        <option value="active">Active</option>
        <option value="inactive">Deactivated</option>
        <option value="all">All</option>
      </Dropdown>
    </div>
  </div>
);

const RolesTableHeader = ({ count }) => (
  <h3 className="text-lg font-medium text-gray-900">
    {count} Role{count === 1 ? '' : 's'}
  </h3>
);

// "appraisals:read_department" -> appraisals:<wbr>read_<wbr>department
const breakableKey = (key) => key.match(/[^:_]+[:_]?|[:_]/g)
  .flatMap((part, i) => (i === 0 ? [part] : [<wbr key={i} />, part]));

const UserManagement = () => {
  const { user: currentUser, hasPermission, hasRole } = useAuth();

  const canViewUsers = hasPermission('users', 'read');
  const canViewRoles = hasPermission('roles', 'read') || hasRole('super_admin');

  const defaultTab = canViewUsers ? 'users' : 'roles';
  const [storedTab, setActiveTab] = usePersistedState('users.tab', defaultTab);
  // The stored tab can outlive the session that picked it - a different user
  // may sign in on the same browser tab - so check it against this user.
  const canOpenStoredTab = storedTab === 'roles' ? canViewRoles : canViewUsers;
  const activeTab = canOpenStoredTab ? storedTab : defaultTab;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  // Save refused locally: every user needs at least one role.
  const [rolesError, setRolesError] = useState('');
  const [form, setForm] = useState({ username: '', email: '', password: '', employee_id: '' });
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  // The roles the user held when the dialog opened. One the caller may not assign stays
  // exactly as it was: it is shown checked and locked, and sent back unchanged.
  const [heldRoleIds, setHeldRoleIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmTargetUser, setConfirmTargetUser] = useState(null);
  const [confirmAction, setConfirmAction] = useState('');

  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '' });
  const [showPermModal, setShowPermModal] = useState(false);
  const [permRole, setPermRole] = useState(null);
  const [permLoading, setPermLoading] = useState(false);
  // permLoading also covers opening; only a save must not be walked away from.
  const [permSaving, setPermSaving] = useState(false);
  // The user popup opens on its first field: the employee link when adding,
  // the username when editing.
  const employeeLinkField = useRef(null);
  const usernameField = useRef(null);
  // The edit dialog opens before the user's current roles arrive. Until they do, the
  // checkboxes read "no roles", and saving then would replace every role with none.
  const [rolesLoading, setRolesLoading] = useState(false);
  // The roles could not be read: save the other fields but leave the roles alone
  // (PUT /api/users/:id changes roles only when role_ids is sent).
  const [rolesLoadFailed, setRolesLoadFailed] = useState(false);
  const [rolePerms, setRolePerms] = useState([]);
  // The permissions the role held when its dialog opened (see heldRoleIds).
  const [heldPermIds, setHeldPermIds] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetRole, setDeleteTargetRole] = useState(null);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [deleteTargetUser, setDeleteTargetUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  // Default: hide deactivated credentials so admins aren't distracted by
  // historical accounts. Switch to 'inactive' or 'all' explicitly.
  const [userStatusFilter, setUserStatusFilter] = usePersistedState(
    'users.status',
    'active'
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStatusFilter]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, rolesRes, permsRes] = await Promise.allSettled([
        api.get('/api/users', { params: { status: userStatusFilter } }),
        api.get('/api/rbac/roles'),
        api.get('/api/rbac/permissions'),
      ]);
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data.data);
      else setError('Failed to fetch users');
      if (rolesRes.status === 'fulfilled') setRoles(rolesRes.value.data.data);
      if (permsRes.status === 'fulfilled') setPermissions(permsRes.value.data.data);
    } catch {
      setError('Failed to fetch data');
    }
    setLoading(false);
  };

  // Every write can be refused by the role ceiling when the flags this page acted on have
  // gone stale (someone changed a role meanwhile): show the server's reason, and reload
  // the flags so the page stops offering the refused action.
  const reportWriteError = (err, fallback) => {
    const data = err?.response?.data;
    const message = data?.message || data?.errors?.[0]?.msg || fallback;
    toast.error(message);
    if (data?.code === 'ROLE_CEILING') fetchData();
    return message;
  };

  const fetchDepartments = async () => {
    try {
      const res = await api.get('/api/departments');
      setDepartments(res.data.data || []);
    } catch {
      setDepartments([]);
    }
  };

  // A user's employee link and department change only for a caller holding
  // employees:update_all, acting on someone else they may manage (the server refuses the
  // rest). Returns why they are locked, or null when the caller may change them.
  const linkLockReasonFor = (user) => {
    if (!user) return null;
    if (user.id === currentUser?.id) return OWN_LINK_REASON;
    if (!hasPermission('employees', 'update_all') || !canManageUser(user)) return LINK_GRANT_REASON;
    return null;
  };

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/api/employees/without-user');
      setEmployees(res.data.data);
    } catch {
      setEmployees([]);
    }
  };

  // The user dialog's roles and the role dialog's permissions are fetched as each dialog
  // opens. Only the latest open may fill them: a response for a dialog that was since
  // closed or reopened on someone else would otherwise tick that other user's roles (or
  // that other role's permissions), and Save would write them.
  const beginUserRolesLoad = useLatestRequest();
  const beginRolePermsLoad = useLatestRequest();

  const openAddUser = async () => {
    beginUserRolesLoad();
    setRolesLoading(false);
    setRolesLoadFailed(false);
    setEditingUser(null);
    setForm({ username: '', email: '', personal_email: '', password: '', employee_id: '' });
    setRolesError('');
    // POST /api/users gives a user sent no roles the employee role, so start there: what
    // HR sees is what the server will do.
    const employeeRole = roles.find(r => r.name === 'employee' && canAssignRole(r));
    setSelectedRoleIds(employeeRole ? [employeeRole.id] : []);
    setHeldRoleIds([]);
    setShowUserModal(true);
    await fetchEmployees();
  };

  const openEditUser = async (user) => {
    setEditingUser(user);
    setForm({
      username: user.username,
      email: user.email,
      personal_email: user.personal_email || '',
      password: '',
      employee_id: user.employee_id || '',
      department_id: user.department_id || '',
    });
    setRolesError('');
    setShowUserModal(true);
    // The lists to re-link from are only needed by a caller who may re-link.
    if (!linkLockReasonFor(user)) {
      fetchEmployees();
      fetchDepartments();
    }
    setSelectedRoleIds([]);
    setHeldRoleIds([]);
    setRolesLoadFailed(false);
    setRolesLoading(true);
    const isCurrent = beginUserRolesLoad();
    try {
      const res = await api.get(`/api/users/${user.id}/roles`);
      if (isCurrent()) {
        const ids = res.data.data.map(r => r.id);
        setSelectedRoleIds(ids);
        setHeldRoleIds(ids);
      }
    } catch {
      if (isCurrent()) setRolesLoadFailed(true);
    }
    if (isCurrent()) setRolesLoading(false);
  };

  const closeUserModal = () => {
    beginUserRolesLoad();
    setRolesLoading(false);
    setRolesLoadFailed(false);
    setShowUserModal(false);
    setEditingUser(null);
    setForm({ username: '', email: '', personal_email: '', password: '', employee_id: '' });
    setSelectedRoleIds([]);
    setHeldRoleIds([]);
    setRolesError('');
  };

  const handleFormChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const toggleRoleId = (roleId) => {
    if (!canAssignRole(roles.find(r => r.id === roleId))) return;
    setRolesError('');
    setSelectedRoleIds(ids =>
      ids.includes(roleId) ? ids.filter(id => id !== roleId) : [...ids, roleId]
    );
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (rolesLoading) return;
    setError('');

    // Enforce complexity only when the admin actually typed a password.
    // On create with a blank field, the backend auto-generates one and emails it.
    if (form.password && form.password.trim()) {
      const complexity = validatePasswordComplexity(form.password);
      if (!complexity.valid) {
        setError(complexity.error);
        toast.error(complexity.error);
        return;
      }
    }

    // Only roles the caller may assign can change; any other role the user held goes
    // back exactly as it was, so the request never touches it.
    const assignableIds = new Set(roles.filter(canAssignRole).map(r => r.id));
    const roleIds = [
      ...selectedRoleIds.filter(id => assignableIds.has(id)),
      ...heldRoleIds.filter(id => !assignableIds.has(id)),
    ];
    if (!rolesLoadFailed && roleIds.length === 0) {
      setRolesError(NO_ROLE_ERROR);
      return;
    }

    setSaving(true);
    try {
      const payload = rolesLoadFailed ? { ...form } : { ...form, role_ids: roleIds };
      if (editingUser) {
        // The link and department go only when the caller may change them and did:
        // an unchanged value is not sent either, so a save that did not mean to re-link
        // can never be refused for it.
        delete payload.employee_id;
        delete payload.department_id;
        if (!linkLockReasonFor(editingUser)) {
          const changed = (field) => form[field] !== '' && form[field] != null
            && String(form[field]) !== String(editingUser[field] ?? '');
          if (changed('employee_id')) payload.employee_id = Number(form.employee_id);
          if (changed('department_id')) payload.department_id = Number(form.department_id);
        }
      } else {
        if (!payload.employee_id) delete payload.employee_id;
        if (!payload.department_id) delete payload.department_id;
      }

      if (editingUser) {
        if (!payload.password) delete payload.password;
        if (!payload.personal_email) delete payload.personal_email;
        await api.put(`/api/users/${editingUser.id}`, payload);
        toast.success('User updated successfully!');
      } else {
        if (!payload.password) delete payload.password;
        const res = await api.post('/api/users', payload);
        const inviteSent = res?.data?.data?.invite_sent;
        toast.success(
          inviteSent
            ? 'User added — login invite sent to their personal email.'
            : 'User added. (Email delivery is not configured — share credentials manually.)'
        );
      }
      closeUserModal();
      fetchData();
    } catch (err) {
      setError(reportWriteError(err, `Failed to ${editingUser ? 'update' : 'add'} user`));
    }
    setSaving(false);
  };

  const handleDeactivateUser = (user) => {
    if (user.id === currentUser?.id) {
      toast.error('You cannot deactivate your own account');
      return;
    }
    setConfirmTargetUser(user);
    setConfirmAction(user.is_active ? 'deactivate' : 'activate');
    setShowConfirmModal(true);
  };

  const confirmDeactivateUser = async () => {
    if (!confirmTargetUser) return;
    setShowConfirmModal(false);
    try {
      if (confirmAction === 'revoke_invite') {
        await api.post(`/api/users/${confirmTargetUser.id}/revoke-invite`);
        toast.success('Invite revoked');
      } else {
        await api.delete(`/api/users/${confirmTargetUser.id}?action=${confirmAction}`);
        toast.success(`User ${confirmAction}d successfully!`);
      }
      fetchData();
    } catch (err) {
      reportWriteError(err, 'Failed to update user status');
    }
    setConfirmTargetUser(null);
    setConfirmAction('');
  };

  // Derives the lifecycle state of an admin-created account. 'pending' covers
  // the window between creation and first login; 'expired' is a pending invite
  // past its server-side TTL. Active users who've logged in are 'none'.
  const getInviteState = (user) => {
    if (!user || user.last_login || !user.must_change_password) return 'none';
    if (user.invite_revoked_at) return 'revoked';
    if (user.invite_expires_at && new Date(user.invite_expires_at).getTime() <= Date.now()) {
      return 'expired';
    }
    return 'pending';
  };

  const handleResendInvite = async (user) => {
    try {
      const res = await api.post(`/api/users/${user.id}/resend-invite`);
      if (res.data?.data?.invite_sent) {
        toast.success(`Invite re-sent to ${user.username}`);
      } else {
        toast.success('Invite refreshed (email not sent — SendGrid not configured)');
      }
      fetchData();
    } catch (err) {
      reportWriteError(err, 'Failed to resend invite');
    }
  };

  const handleRevokeInvite = (user) => {
    setConfirmTargetUser(user);
    setConfirmAction('revoke_invite');
    setShowConfirmModal(true);
  };

  const handleDeleteUser = (user) => {
    if (user.id === currentUser?.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    setDeleteTargetUser(user);
    setShowDeleteUserModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!deleteTargetUser) return;
    setDeletingUser(true);
    try {
      await api.delete(`/api/users/${deleteTargetUser.id}?action=delete`);
      toast.success('User deleted successfully!');
      setShowDeleteUserModal(false);
      setDeleteTargetUser(null);
      fetchData();
    } catch (err) {
      // Backend returns a `blocking` array on 409 listing the exact FK references
      // (e.g. leave_audit_logs, documents.owner_id) that prevent hard-delete.
      // Surface that so the admin knows why deactivate is the only option.
      const data = err.response?.data;
      if (data?.blocking?.length) {
        toast.error(data.message || "This user can't be permanently deleted. Deactivate them instead.", { duration: 7000 });
      } else {
        reportWriteError(err, 'Failed to delete user');
      }
    }
    setDeletingUser(false);
  };

  const openAddRole = () => {
    setEditingRole(null);
    setRoleForm({ name: '', description: '' });
    setShowRoleForm(true);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({ name: role.name, description: role.description });
    setShowRoleForm(true);
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await api.put(`/api/rbac/roles/${editingRole.id}`, roleForm);
        toast.success('Role updated successfully!');
      } else {
        await api.post('/api/rbac/roles', roleForm);
        toast.success('Role created successfully!');
      }
      setShowRoleForm(false);
      fetchData();
    } catch (err) {
      reportWriteError(err, 'Failed to save role');
    }
  };

  const handleDeleteRole = (role) => {
    setDeleteTargetRole(role);
    setShowDeleteModal(true);
  };

  const confirmDeleteRole = async () => {
    if (!deleteTargetRole) return;
    try {
      await api.delete(`/api/rbac/roles/${deleteTargetRole.id}`);
      toast.success('Role deleted successfully!');
      fetchData();
    } catch (err) {
      reportWriteError(err, 'Failed to delete role');
    }
    setShowDeleteModal(false);
    setDeleteTargetRole(null);
  };

  const openPermModal = async (role) => {
    setPermRole(role);
    setShowPermModal(true);
    setPermLoading(true);
    setHeldPermIds([]);
    const isCurrent = beginRolePermsLoad();
    try {
      const res = await api.get(`/api/rbac/roles/${role.id}/permissions`);
      if (isCurrent()) {
        const ids = res.data.data.map(p => p.id);
        setRolePerms(ids);
        setHeldPermIds(ids);
      }
    } catch {
      if (isCurrent()) setRolePerms([]);
    }
    if (isCurrent()) setPermLoading(false);
  };

  const closePermModal = () => {
    beginRolePermsLoad();
    setPermLoading(false);
    setShowPermModal(false);
    setPermRole(null);
    setRolePerms([]);
    setHeldPermIds([]);
  };

  const grantablePermIds = new Set(permissions.filter(canGrantPermission).map(p => p.id));
  // A role holding any permission the caller lacks is beyond the caller: view only.
  const permRoleReadOnly = !!permRole
    && (!canAssignRole(permRole) || heldPermIds.some(id => !grantablePermIds.has(id)));

  const handlePermChange = (permId) => {
    if (permRoleReadOnly || !grantablePermIds.has(permId)) return;
    setRolePerms(perms =>
      perms.includes(permId) ? perms.filter(id => id !== permId) : [...perms, permId]
    );
  };

  const saveRolePerms = async () => {
    if (permRoleReadOnly) return;
    setPermLoading(true);
    setPermSaving(true);
    try {
      // As with roles: only permissions the caller holds can change.
      const permissionIds = [
        ...rolePerms.filter(id => grantablePermIds.has(id)),
        ...heldPermIds.filter(id => !grantablePermIds.has(id)),
      ];
      await api.put(`/api/rbac/roles/${permRole.id}/permissions`, { permission_ids: permissionIds });
      toast.success('Permissions updated!');
      closePermModal();
      fetchData();
    } catch (err) {
      reportWriteError(err, 'Failed to update permissions');
    }
    setPermLoading(false);
    setPermSaving(false);
  };

  if (!canViewUsers && !canViewRoles) {
    return (
      <div className="w-full mx-auto px-0 py-6">
        <div className="bg-white rounded-lg shadow-soft p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-red-800 font-medium">Access Denied</h3>
            <p className="text-red-600 mt-1">You don't have permission to view this page.</p>
          </div>
        </div>
      </div>
    );
  }

  // The edit dialog's employee link and department: the current values always show, and
  // the lists to pick others from only for a caller who may change them.
  const linkLockReason = linkLockReasonFor(editingUser);
  const linkOptions = editingUser ? [
    ...(editingUser.employee_id ? [{
      value: editingUser.employee_id,
      label: [editingUser.first_name, editingUser.last_name].filter(Boolean).join(' ') || `Employee #${editingUser.employee_id}`,
    }] : []),
    ...employees
      .filter(emp => String(emp.id) !== String(editingUser.employee_id))
      .map(emp => ({ value: emp.id, label: `${emp.employee_id} - ${emp.first_name} ${emp.last_name} (${emp.position})` })),
  ] : [];
  const departmentOptions = editingUser ? [
    ...(editingUser.department_id && !departments.some(d => String(d.id) === String(editingUser.department_id))
      ? [{ id: editingUser.department_id, name: editingUser.department_name || `Department #${editingUser.department_id}` }]
      : []),
    ...departments,
  ] : [];

  const inputClass = 'border border-gray-300 rounded-md px-3 py-2 w-full focus:ring-primary-500 focus:border-primary-500 text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnPrimary = 'inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50';
  const btnSecondary = 'inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500';
  const btnDanger = 'inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-danger-600 hover:bg-danger-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-500';

  const userColumns = [
    {
      key: 'username',
      header: 'Username',
      cellClassName: 'font-medium',
      render: (user) => (
        <span data-testid="users-cell-username">
          {user.username}
          {user.id === currentUser?.id && <span className="ml-2 text-xs text-blue-600">(You)</span>}
        </span>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (user) => user.email,
    },
    {
      key: 'roles',
      header: 'Roles',
      cellClassName: 'whitespace-normal',
      render: (user) => {
        const userRoleNames = (user.roles || []).filter(r => r);
        return (
          <div className="flex flex-wrap gap-1">
            {userRoleNames.length > 0 ? userRoleNames.map((roleName, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {formatRoleName(roleName)}
              </span>
            )) : (
              <span className="text-xs text-gray-400">No roles</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (user) => {
        const inviteState = getInviteState(user);
        return !user.is_active ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Inactive
          </span>
        ) : inviteState === 'revoked' ? (
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700"
            title={`Invite revoked ${new Date(user.invite_revoked_at).toLocaleString()}`}
          >
            Invite revoked
          </span>
        ) : inviteState === 'expired' ? (
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
            title={`Invite expired ${new Date(user.invite_expires_at).toLocaleString()}`}
          >
            Invite expired
          </span>
        ) : inviteState === 'pending' ? (
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
            title={user.invite_expires_at ? `Expires ${new Date(user.invite_expires_at).toLocaleString()}` : 'Pending first login'}
          >
            Pending invite
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      cellClassName: 'font-medium',
      render: (user) => {
        const isCurrentUser = user.id === currentUser?.id;
        const inviteState = getInviteState(user);
        // Every action here writes to this user; the server refuses them all on a user
        // with more access than the caller, so they are shown, disabled, with the reason.
        const locked = !canManageUser(user);
        const actionProps = (label) => ({
          'aria-label': label,
          title: locked ? USER_REASON : label,
          disabled: locked,
        });
        const lockedClass = 'disabled:text-gray-300 disabled:bg-transparent disabled:cursor-not-allowed';
        const resendLabel = inviteState === 'revoked' ? 'Resend invite (reissue)'
          : inviteState === 'expired' ? 'Resend invite (expired)' : 'Resend invite';
        return (
          <div className="flex justify-end gap-2">
            {hasPermission('users', 'update') && (
              <button
                onClick={() => openEditUser(user)}
                className={`p-1.5 text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded ${lockedClass}`}
                {...actionProps('Edit')}
              >
                <Edit className="h-4 w-4" aria-hidden />
              </button>
            )}
            {hasPermission('users', 'update') && user.is_active && inviteState !== 'none' && (
              <button
                data-testid="users-resend-invite"
                onClick={() => handleResendInvite(user)}
                className={`p-1.5 rounded text-primary-600 hover:text-primary-800 hover:bg-primary-50 ${lockedClass}`}
                {...actionProps(resendLabel)}
              >
                <Send className="h-4 w-4" aria-hidden />
              </button>
            )}
            {hasPermission('users', 'update') && user.is_active && (inviteState === 'pending' || inviteState === 'expired') && !isCurrentUser && (
              <button
                data-testid="users-revoke-invite"
                onClick={() => handleRevokeInvite(user)}
                className={`p-1.5 rounded text-danger-600 hover:text-danger-800 hover:bg-danger-50 ${lockedClass}`}
                {...actionProps('Revoke invite')}
              >
                <Ban className="h-4 w-4" aria-hidden />
              </button>
            )}
            {hasPermission('users', 'delete') && (
              <button
                data-testid="users-toggle-active"
                onClick={() => handleDeactivateUser(user)}
                className={`p-1.5 rounded ${user.is_active ? 'text-danger-600 hover:text-danger-800 hover:bg-danger-50' : 'text-success-600 hover:text-success-800 hover:bg-success-50'} ${lockedClass}`}
                {...actionProps(user.is_active ? 'Deactivate' : 'Activate')}
              >
                {user.is_active ? <UserMinus className="h-4 w-4" aria-hidden /> : <UserPlus className="h-4 w-4" aria-hidden />}
              </button>
            )}
            {hasPermission('users', 'delete') && user.id !== currentUser?.id && (
              <button
                data-testid="users-delete"
                onClick={() => handleDeleteUser(user)}
                className={`p-1.5 rounded text-danger-600 hover:text-white hover:bg-danger-600 ${lockedClass}`}
                {...actionProps('Delete permanently')}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const roleColumns = [
    {
      key: 'name',
      header: 'Role Name',
      // The role ADMIN table, so it shows both: the human label to read, and the
      // raw identifier underneath, which the create/edit form and the RBAC seeds use.
      render: (role) => (
        <>
          <div className="font-medium text-gray-900">{formatRoleName(role.name)}</div>
          <div className="text-xs text-gray-400 font-mono">{role.name}</div>
        </>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      cellClassName: 'whitespace-normal text-gray-500',
      render: (role) => role.description,
    },
    {
      key: 'type',
      header: 'Type',
      render: (role) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          role.is_system_role ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {role.is_system_role ? 'System' : 'Custom'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      cellClassName: 'font-medium',
      render: (role) => {
        // A role holding any permission the caller lacks is beyond the caller: its
        // permissions open read-only, and it cannot be renamed or deleted.
        const readOnly = !canAssignRole(role);
        const permsLabel = readOnly ? 'View permissions' : 'Manage Permissions';
        const lockedClass = 'disabled:text-gray-300 disabled:bg-transparent disabled:cursor-not-allowed';
        return (
          <div className="flex justify-end gap-2">
            <button
              onClick={!role.is_system_role ? () => openPermModal(role) : undefined}
              className={`p-1.5 rounded ${role.is_system_role ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-primary-700 hover:bg-primary-50'}`}
              aria-label={role.is_system_role ? 'Manage Permissions' : permsLabel}
              title={role.is_system_role ? 'Cannot edit system role permissions' : permsLabel}
              disabled={role.is_system_role}
            >
              <Settings className="h-4 w-4" aria-hidden />
            </button>
            {hasPermission('roles', 'update') && !role.is_system_role && (
              <button
                onClick={() => openEditRole(role)}
                className={`p-1.5 text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded ${lockedClass}`}
                aria-label="Edit"
                title={readOnly ? ROLE_REASON : 'Edit'}
                disabled={readOnly}
              >
                <Edit className="h-4 w-4" aria-hidden />
              </button>
            )}
            {hasPermission('roles', 'delete') && !role.is_system_role && (
              <button
                onClick={() => handleDeleteRole(role)}
                className={`p-1.5 text-danger-600 hover:text-danger-800 hover:bg-danger-50 rounded ${lockedClass}`}
                aria-label="Delete"
                title={readOnly ? ROLE_REASON : 'Delete'}
                disabled={readOnly}
              >
                <Trash className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="w-full mx-auto px-0 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users & Roles</h1>
          <p className="text-gray-600">Manage system users and role-based access control.</p>
        </div>
        {activeTab === 'users' && hasPermission('users', 'create') && (
          // Waits for the role list: the dialog's defaults (Employee ticked) come from it.
          <button data-testid="users-add-btn" onClick={openAddUser} className={btnPrimary} disabled={loading}>
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </button>
        )}
        {activeTab === 'roles' && hasPermission('roles', 'create') && (
          <button data-testid="rbac-add-btn" onClick={openAddRole} className={btnPrimary}>
            <Plus className="h-4 w-4 mr-2" />
            Add Role
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {canViewUsers && (
            <button
              data-testid="users-tab"
              onClick={() => setActiveTab('users')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UsersIcon className="inline h-4 w-4 mr-2" />
              Users
            </button>
          )}
          {canViewRoles && (
            <button
              data-testid="rbac-tab"
              onClick={() => setActiveTab('roles')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'roles'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Shield className="inline h-4 w-4 mr-2" />
              Roles
            </button>
          )}
        </nav>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
        </div>
      ) : (
        <>
          {/* USERS TAB */}
          {activeTab === 'users' && canViewUsers && (
            <CTable
              header={
                <UsersTableHeader
                  count={users.length}
                  statusFilter={userStatusFilter}
                  onStatusChange={(e) => setUserStatusFilter(e.target.value)}
                />
              }
              data={users}
              rowKey="id"
              getRowProps={(user) => ({
                'data-testid': `users-row-${user.id}`,
                'data-testid-group': 'users-row',
                className: `hover:bg-gray-50 ${user.id === currentUser?.id ? 'bg-blue-50' : ''}`.trim(),
              })}
              columns={userColumns}
            />
          )}

          {/* ROLES TAB */}
          {activeTab === 'roles' && canViewRoles && (
            <div>
              {showRoleForm && hasPermission('roles', editingRole ? 'update' : 'create') && (
                <div className="mb-6 bg-white rounded-lg shadow-soft overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                      {editingRole ? 'Edit Role' : 'Add New Role'}
                    </h3>
                  </div>
                  <form onSubmit={handleRoleSubmit} className="p-6 space-y-4">
                    <div>
                      <label className={labelClass}>Role Name</label>
                      <input
                        type="text"
                        name="name"
                        value={roleForm.name}
                        onChange={e => setRoleForm(f => ({ ...f, [e.target.name]: e.target.value }))}
                        className={inputClass}
                        required
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Description</label>
                      <textarea
                        name="description"
                        value={roleForm.description}
                        onChange={e => setRoleForm(f => ({ ...f, [e.target.name]: e.target.value }))}
                        className={inputClass}
                        rows="3"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" data-testid="rbac-submit" className={btnPrimary}>
                        {editingRole ? 'Update' : 'Create'}
                      </button>
                      <button type="button" onClick={() => setShowRoleForm(false)} className={btnSecondary}>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <CTable
                header={<RolesTableHeader count={roles.length} />}
                data={roles}
                rowKey="id"
                getRowProps={(role) => ({
                  'data-testid': `rbac-row-${role.id}`,
                  'data-testid-group': 'rbac-row',
                  className: 'hover:bg-gray-50',
                })}
                columns={roleColumns}
              />
            </div>
          )}
        </>
      )}

      {/* USER ADD/EDIT MODAL */}
      {showUserModal && (
        <CModal
          onClose={closeUserModal}
          labelledBy="user-modal-title"
          initialFocus={editingUser ? usernameField : employeeLinkField}
          dismissible={!saving}
          closeOnBackdrop={false}
          className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 relative"
        >
          <button
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold leading-none"
            onClick={closeUserModal}
            aria-label="Close"
          >
            &times;
          </button>
          <h2 id="user-modal-title" className="text-xl font-bold mb-4 text-gray-900">
            {editingUser ? `Edit User: ${editingUser.username}` : 'Add New User'}
          </h2>
          <form onSubmit={handleSaveUser}>
            <div className="space-y-4">
              {!editingUser && (
                <div>
                  <label id="users-field-employee-link-label" className={labelClass}>
                    Link to Employee <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <SearchableSelect
                    ref={employeeLinkField}
                    aria-labelledby="users-field-employee-link-label"
                    value={form.employee_id}
                    onChange={(val) => {
                      const emp = employees.find(e => String(e.id) === String(val));
                      if (emp) {
                        setForm(f => ({
                          ...f,
                          employee_id: val,
                          username: emp.email ? emp.email.split('@')[0] : '',
                          email: emp.email || '',
                          personal_email: emp.personal_email || f.personal_email,
                        }));
                      } else {
                        setForm(f => ({ ...f, employee_id: '' }));
                      }
                    }}
                    options={employees.map(emp => ({
                      value: emp.id,
                      label: `${emp.employee_id} - ${emp.first_name} ${emp.last_name} (${emp.position})`,
                    }))}
                    placeholder="Leave blank — employee will complete onboarding"
                  />
                </div>
              )}
              <div>
                <label htmlFor="users-field-username" className={labelClass}>Username</label>
                <input ref={usernameField} id="users-field-username" name="username" data-testid="users-field-username" value={form.username} onChange={handleFormChange} className={inputClass} required />
              </div>
              <div>
                <label htmlFor="users-field-email" className={labelClass}>Work email</label>
                <input id="users-field-email" name="email" data-testid="users-field-email" type="email" value={form.email} onChange={handleFormChange} className={inputClass} required />
                <p className="mt-1 text-xs text-gray-500">
                  Must be a company address (e.g. you@bytecorp.io).
                </p>
              </div>
              <div>
                <label htmlFor="users-field-personal-email" className={labelClass}>Personal email{editingUser ? '' : ' *'}</label>
                <input
                  id="users-field-personal-email"
                  name="personal_email"
                  data-testid="users-field-personal-email"
                  type="email"
                  value={form.personal_email}
                  onChange={handleFormChange}
                  className={inputClass}
                  required={!editingUser}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {editingUser
                    ? 'Where login-related emails will be delivered.'
                    : 'The login invite will be sent here — the candidate cannot access their work inbox yet.'}
                </p>
              </div>
              {editingUser && (
                <div className="space-y-4">
                  <div>
                    <label id="users-field-employee-label" className={labelClass}>Linked employee</label>
                    <SearchableSelect
                      aria-labelledby="users-field-employee-label"
                      data-testid="users-field-employee"
                      value={form.employee_id}
                      onChange={(val) => setForm(f => ({ ...f, employee_id: val || '' }))}
                      options={linkOptions}
                      placeholder="Not linked to an employee"
                      disabled={!!linkLockReason}
                    />
                  </div>
                  <div>
                    <label htmlFor="users-field-department" className={labelClass}>Department</label>
                    <Dropdown
                      id="users-field-department"
                      data-testid="users-field-department"
                      value={form.department_id === '' || form.department_id == null ? '' : String(form.department_id)}
                      onChange={(e) => setForm(f => ({ ...f, department_id: e.target.value }))}
                      disabled={!!linkLockReason}
                    >
                      {!editingUser.department_id && <option value="">No department</option>}
                      {departmentOptions.map(d => (
                        <option key={d.id} value={String(d.id)}>{d.name}</option>
                      ))}
                    </Dropdown>
                  </div>
                  {linkLockReason && (
                    <p className="-mt-2 text-xs text-gray-500">{linkLockReason}</p>
                  )}
                </div>
              )}
              <div>
                <label htmlFor="users-field-password" className={labelClass}>
                  Password{editingUser ? ' (leave blank to keep current)' : ' (optional)'}
                </label>
                <PasswordInput
                  id="users-field-password"
                  name="password"
                  data-testid="users-field-password"
                  value={form.password}
                  onChange={handleFormChange}
                  autoComplete="new-password"
                  placeholder={editingUser ? 'Leave blank to keep current' : 'Leave blank to auto-generate and email'}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {editingUser
                    ? 'At least 8 characters, including one number and one symbol.'
                    : 'Leave blank to auto-generate a temporary password and email it to the personal address. The user will be required to change it on first login.'}
                </p>
              </div>

              {/* Role Assignment */}
              <div>
                <label className={labelClass}>Roles</label>
                <p className="-mt-0.5 mb-1 text-xs text-gray-500">A user needs at least one role.</p>
                <div data-testid="users-field-roles" className="border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  {roles.length > 0 ? roles.map(role => {
                    const locked = !canAssignRole(role);
                    const reason = heldRoleIds.includes(role.id) ? REMOVE_REASON : ASSIGN_REASON;
                    return (
                      <label
                        key={role.id}
                        className={`flex items-start gap-2 text-sm rounded px-1 py-1 ${locked ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRoleIds.includes(role.id)}
                          onChange={() => toggleRoleId(role.id)}
                          disabled={rolesLoading || rolesLoadFailed || locked}
                          className="accent-primary-600 mt-0.5 shrink-0 disabled:opacity-50"
                        />
                        {/* Stacked, not inline: descriptions are long enough to wrap,
                            and an inline label+description on one flex line left the
                            checkbox centred against a two-line block. */}
                        <span className="min-w-0">
                          <span className={`block font-medium ${locked ? 'text-gray-500' : 'text-gray-900'}`}>{formatRoleName(role.name)}</span>
                          {role.description && (
                            <span className="block text-gray-400 text-xs">{role.description}</span>
                          )}
                          {locked && (
                            <span className="block text-xs text-gray-500">{reason}</span>
                          )}
                        </span>
                      </label>
                    );
                  }) : (
                    <p className="text-sm text-gray-400">No roles available</p>
                  )}
                </div>
                {rolesLoading && (
                  <p className="mt-1 text-xs text-gray-500" role="status">Loading this user's roles...</p>
                )}
                {rolesLoadFailed && (
                  <p className="mt-1 text-xs text-danger-600" role="status">
                    This user's roles could not be loaded, so they will be left as they are.
                  </p>
                )}
                {rolesError && (
                  <p className="mt-1 text-xs text-danger-600" role="alert">{rolesError}</p>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button type="submit" data-testid="users-submit" className={btnPrimary} disabled={saving || rolesLoading}>
                {saving ? 'Saving...' : editingUser ? 'Update' : 'Save'}
              </button>
              <button type="button" className={btnSecondary} onClick={closeUserModal}>
                Cancel
              </button>
            </div>
          </form>
        </CModal>
      )}

      {/* DEACTIVATE/ACTIVATE CONFIRM MODAL */}
      {showConfirmModal && confirmTargetUser && (
        <CModal
          onClose={() => setShowConfirmModal(false)}
          labelledBy="user-confirm-title"
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
          data-testid="users-confirm-modal"
        >
          <button
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold leading-none"
            onClick={() => setShowConfirmModal(false)}
            aria-label="Close"
          >
            &times;
          </button>
          <h2 id="user-confirm-title" className="text-xl font-bold mb-4 text-gray-900">
            {confirmAction === 'deactivate' ? 'Deactivate User'
              : confirmAction === 'revoke_invite' ? 'Revoke Invite'
              : 'Activate User'}
          </h2>
          <p className="mb-4 text-gray-700">
            {confirmAction === 'revoke_invite' ? (
              <>
                Revoke the pending invite for <strong>{confirmTargetUser.username}</strong>? This invalidates
                the temp password so the previously-emailed copy can no longer be used. The account stays
                visible under Active users with an "Invite revoked" badge — click <em>Resend invite</em> to
                issue a fresh password, or <em>Deactivate</em> to pull it out of the active list.
              </>
            ) : (
              <>Are you sure you want to {confirmAction} <strong>{confirmTargetUser.username}</strong>?</>
            )}
          </p>
          <div className="flex justify-end gap-2">
            <button data-testid="users-confirm-no" className={btnSecondary} onClick={() => setShowConfirmModal(false)}>Cancel</button>
            <button
              data-testid="users-confirm-yes"
              className={(confirmAction === 'deactivate' || confirmAction === 'revoke_invite') ? btnDanger : btnPrimary}
              onClick={confirmDeactivateUser}
            >
              {confirmAction === 'deactivate' ? 'Deactivate'
                : confirmAction === 'revoke_invite' ? 'Revoke invite'
                : 'Activate'}
            </button>
          </div>
        </CModal>
      )}

      {/* HARD DELETE USER CONFIRM MODAL */}
      {showDeleteUserModal && deleteTargetUser && (
        <CModal
          onClose={() => { setShowDeleteUserModal(false); setDeleteTargetUser(null); }}
          labelledBy="delete-user-title"
          dismissible={!deletingUser}
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        >
          <button
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold leading-none"
            onClick={() => { setShowDeleteUserModal(false); setDeleteTargetUser(null); }}
            aria-label="Close"
          >
            &times;
          </button>
          <h2 id="delete-user-title" className="text-xl font-bold mb-2 text-danger-700">Delete user permanently</h2>
          <p className="mb-2 text-gray-700">
            This permanently removes the login for <strong>{deleteTargetUser.username}</strong>.
            The linked employee record, if any, is not affected.
          </p>
          <p className="mb-4 text-sm text-gray-500">
            Users who have reviewed or approved anything, created reports, or own documents
            cannot be hard-deleted — deactivate them instead.
          </p>
          <div className="flex justify-end gap-2">
            <button
              className={btnSecondary}
              onClick={() => { setShowDeleteUserModal(false); setDeleteTargetUser(null); }}
              disabled={deletingUser}
            >
              Cancel
            </button>
            <button
              className={btnDanger}
              onClick={confirmDeleteUser}
              disabled={deletingUser}
            >
              {deletingUser ? 'Deleting…' : 'Delete permanently'}
            </button>
          </div>
        </CModal>
      )}

      {/* PERMISSION MANAGEMENT MODAL */}
      {showPermModal && permRole && (
        <CModal
          onClose={closePermModal}
          labelledBy="role-permissions-title"
          dismissible={!permSaving}
          closeOnBackdrop={false}
          className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-8 relative"
        >
          <button aria-label="Close" className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold" onClick={closePermModal}>&times;</button>
          <h3 id="role-permissions-title" className="text-xl font-semibold mb-6">
            {permRoleReadOnly ? 'Permissions for' : 'Manage Permissions for'}{' '}
            <span className="text-primary-700">{permRole.name}</span>
          </h3>
          {permLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : (
            <form onSubmit={e => { e.preventDefault(); saveRolePerms(); }}>
              {permRoleReadOnly ? (
                <p className="mb-4 text-sm text-gray-600" role="status">{ROLE_REASON}</p>
              ) : permissions.some(perm => !canGrantPermission(perm)) && (
                <p className="mb-4 text-sm text-gray-600">{PERMISSION_REASON} Those are locked.</p>
              )}
              <div className="max-h-[420px] overflow-y-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6 pr-2">
                {permissions.map(perm => {
                  const locked = permRoleReadOnly || !canGrantPermission(perm);
                  return (
                    <label
                      key={perm.id}
                      className={`flex items-start gap-2 text-sm bg-gray-50 rounded p-2 ${locked ? 'cursor-not-allowed' : ''}`}
                      title={locked ? (permRoleReadOnly ? ROLE_REASON : PERMISSION_REASON) : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={rolePerms.includes(perm.id)}
                        onChange={() => handlePermChange(perm.id)}
                        disabled={locked}
                        className="accent-primary-600 mt-1 disabled:opacity-50"
                      />
                      {/* `appraisals:read_department` is one token wider than a third of the
                          dialog and ran into the next card. <wbr> after each ':' and '_' lets it
                          wrap between segments; the checkbox's accessible name is unchanged. */}
                      <div className="min-w-0">
                        <span className={`font-medium ${locked ? 'text-gray-500' : ''}`}>{breakableKey(`${perm.resource}:${perm.action}`)}</span>
                        <div className="text-gray-400 text-xs">{perm.description}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" className={btnSecondary} onClick={closePermModal}>
                  {permRoleReadOnly ? 'Close' : 'Cancel'}
                </button>
                <button type="submit" className={btnPrimary} disabled={permRoleReadOnly}>Save</button>
              </div>
            </form>
          )}
        </CModal>
      )}

      {/* DELETE ROLE CONFIRM MODAL */}
      {showDeleteModal && deleteTargetRole && (
        <CModal
          onClose={() => setShowDeleteModal(false)}
          labelledBy="delete-role-title"
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setShowDeleteModal(false)}
          >
            &times;
          </button>
          <h2 id="delete-role-title" className="text-xl font-bold mb-4 text-danger-700">Delete Role</h2>
          <p className="mb-4 text-gray-700">
            Are you sure you want to delete the role <strong>{deleteTargetRole.name}</strong>?
          </p>
          <div className="flex justify-end gap-2">
            <button className={btnSecondary} onClick={() => setShowDeleteModal(false)}>Cancel</button>
            <button className={btnDanger} onClick={confirmDeleteRole}>Delete</button>
          </div>
        </CModal>
      )}
    </div>
  );
};

export default UserManagement;
